/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {Box} from "@mui/material";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import {Capability} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/Capability";
import {QInstance} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QInstance";
import {QMenu} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QMenu";
import {QMenuItem} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QMenuItem";
import {QProcessMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QProcessMetaData";
import {QTableMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableMetaData";
import {QRecord} from "@temple-of-syrinx/qqq-frontend-core/lib/model/QRecord";
import React, {useState} from "react";


export interface ActionMenuActions
{
   new?: (closeMenu?: () => void) => void;
   copy?: (closeMenu?: () => void) => void;
   edit?: (closeMenu?: () => void) => void;
   developerMode?: (closeMenu?: () => void) => void;
   audit?: (closeMenu?: () => void) => void;
   delete?: (closeMenu?: () => void) => void;
   runProcess?: (processName: string) => void;
   downloadFileFromField?: (fieldName: string, closeMenu?: () => void) => void;
   bulkLoad?: () => void;
   bulkEdit?: () => void;
   bulkEditWithFile?: () => void;
   bulkDelete?: () => void;

   getMetaData?: () => QInstance;
   getTableProcesses?: () => QProcessMetaData[];
   getGenericProcesses?: () => QProcessMetaData[];
}


/***************************************************************************
 * map a built-in option name to the action callback(s) it requires.  used to
 * decide whether the current screen is wired to handle a built-in - if not,
 * the menu item is suppressed (with a console warning) rather than crashing
 * on click.
 ***************************************************************************/
function isBuiltInHandlerAvailable(option: string, actions: ActionMenuActions): boolean
{
   switch (option)
   {
      case "NEW":                     return actions.new != null;
      case "COPY":                    return actions.copy != null;
      case "EDIT":                    return actions.edit != null;
      case "DELETE":                  return actions.delete != null;
      case "DEVELOPER_MODE":          return actions.developerMode != null;
      case "AUDIT":                   return actions.audit != null;
      case "BULK_LOAD":               return actions.bulkLoad != null;
      case "BULK_EDIT":               return actions.bulkEdit != null;
      case "BULK_EDIT_WITH_FILE":     return actions.bulkEditWithFile != null;
      case "BULK_DELETE":             return actions.bulkDelete != null;
      case "THIS_TABLE_PROCESS_LIST": return actions.runProcess != null && actions.getTableProcesses != null;
      case "ALL_TABLES_PROCESS_LIST": return actions.runProcess != null && actions.getGenericProcesses != null;
      default:                        return false;
   }
}


/***************************************************************************
 * check if a menu item would render anything visible given the current
 * table capabilities, permissions, and available action handlers.
 ***************************************************************************/
function wouldItemRender(item: QMenuItem, table: QTableMetaData, actions: ActionMenuActions): boolean
{
   switch (item.itemType)
   {
      case "DIVIDER":
         return true;
      case "RUN_PROCESS":
      {
         const processName = item.values?.get("processName") as string;
         return actions.runProcess != null && actions.getMetaData()?.processes?.has(processName);
      }
      case "DOWNLOAD_FILE":
         return actions.downloadFileFromField != null;
      case "SUB_LIST":
      case "SUB_MENU":
      {
         const subItems = item.values?.get("items");
         if (!subItems || subItems.length === 0) return false;
         return subItems.some((sub: any) => wouldItemRender(new QMenuItem(sub), table, actions));
      }
      case "BUILT_IN":
      {
         const option = item.values?.get("option") as string;
         if (!isBuiltInHandlerAvailable(option, actions)) return false;

         switch (option)
         {
            case "NEW":
            case "COPY":
               return table?.capabilities.has(Capability.TABLE_INSERT) && table?.insertPermission;
            case "EDIT":
               return table?.capabilities.has(Capability.TABLE_UPDATE) && table?.editPermission;
            case "DELETE":
               return table?.capabilities.has(Capability.TABLE_DELETE) && table?.deletePermission;
            case "BULK_LOAD":
               return table?.capabilities.has(Capability.TABLE_INSERT) && table?.insertPermission && actions.getMetaData()?.processes?.has(`${table.name}.bulkInsert`);
            case "BULK_EDIT":
               return table?.capabilities.has(Capability.TABLE_UPDATE) && table?.editPermission && actions.getMetaData()?.processes?.has(`${table.name}.bulkEdit`);
            case "BULK_EDIT_WITH_FILE":
               return table?.capabilities.has(Capability.TABLE_UPDATE) && table?.editPermission && actions.getMetaData()?.processes?.has(`${table.name}.bulkEditWithFile`);
            case "BULK_DELETE":
               return table?.capabilities.has(Capability.TABLE_DELETE) && table?.deletePermission && actions.getMetaData()?.processes?.has(`${table.name}.bulkDelete`);
            case "AUDIT":
               return actions.getMetaData()?.tables?.has("audit");
            case "THIS_TABLE_PROCESS_LIST":
            case "ALL_TABLES_PROCESS_LIST":
            case "DEVELOPER_MODE":
               return true;
            default:
               return false;
         }
      }
      default:
         return false;
   }
}


//////////////////////////////////////
// component for a single menu item //
//////////////////////////////////////
interface ActionMenuItemProps
{
   tableMetaData: QTableMetaData,
   menuItem: QMenuItem,
   record?: QRecord,
   actions: ActionMenuActions,
   closeMenu?: () => void,
   itemsShown?: ItemsShownInMenu
   keyPrefix?: string
   accordionStorageScope?: "query" | "view"
}

export function ActionMenuItem({tableMetaData: table, menuItem, record, actions, closeMenu, itemsShown, keyPrefix, accordionStorageScope = "view"}: ActionMenuItemProps): JSX.Element
{
   ////////////////////////////////////////////////////////////////////////////////////
   // a Menu using keepMounted renders us before tableMetaData has loaded — bail     //
   // out until then.  Otherwise downstream sub-menus dereference tableMetaData.name //
   // (e.g., the accordion storage key) and crash with an intermittent NPE.          //
   ////////////////////////////////////////////////////////////////////////////////////
   if (!table)
   {
      return <></>;
   }

   function getItemsFromValues(menuItem: QMenuItem): QMenuItem[]
   {
      const itemsObject = menuItem?.values?.get("items");
      if (itemsObject && itemsObject.length)
      {
         const qMenuItems: QMenuItem[] = [];
         for (let itemsObjectElement of itemsObject)
         {
            qMenuItems.push(new QMenuItem(itemsObjectElement));
         }
         return qMenuItems;
      }

      return [];
   }

   const fieldName = menuItem?.values?.get("fieldName") as string;
   const processName = menuItem?.values?.get("processName") as string;
   const option = menuItem?.values?.get("option") as string;
   const items = getItemsFromValues(menuItem);
   const fieldValue = record?.values?.get(fieldName) ?? null;

   switch (menuItem.itemType)
   {
      case "DIVIDER":
      {
         if (!itemsShown || itemsShown?.okayToShowDivider())
         {
            itemsShown?.addItem(menuItem);
            return <Divider />;
         }

         return <></>;
      }
      case "RUN_PROCESS":
      {
         if (actions.runProcess == null)
         {
            console.warn(`No runProcess handler available - suppressing RUN_PROCESS menu item [${processName}].`);
            return <></>;
         }
         /////////////////////////////////////////////////////////////////////////////////////////
         // if the process isn't in this user's metaData (likely permissions), don't render the //
         // item at all - otherwise the user would see a menu item that silently does nothing.  //
         /////////////////////////////////////////////////////////////////////////////////////////
         if (!actions.getMetaData()?.processes?.has(processName))
         {
            console.warn(`Process [${processName}] not found in metaData - suppressing RUN_PROCESS menu item.`);
            return <></>;
         }
         itemsShown?.addItem(menuItem);
         return <MenuItem onClick={() =>
         {
            closeMenu?.();
            actions.runProcess(processName);
         }}>{content(menuItem, "arrow_forward", processName)}</MenuItem>;
      }
      case "DOWNLOAD_FILE":
      {
         if (actions.downloadFileFromField == null)
         {
            console.warn(`No downloadFileFromField handler available - suppressing DOWNLOAD_FILE menu item [${fieldName}].`);
            return <></>;
         }
         itemsShown?.addItem(menuItem);
         return <MenuItem disabled={!fieldValue} onClick={() => actions.downloadFileFromField(fieldName, closeMenu)}>{content(menuItem, "file_download", menuItem.label)}</MenuItem>;
      }
      case "SUB_LIST":
      {
         /////////////////////////////////////////////////////////////////////////////////////
         // do NOT pre-add sub-items to itemsShown here - each sub-item only adds itself if //
         // it actually renders.  pre-adding caused okayToShowDivider() to think items had  //
         // rendered when permission checks were really suppressing them, and a following   //
         // DIVIDER would then leak through as the first visible element of the menu.       //
         /////////////////////////////////////////////////////////////////////////////////////
         return <> {items?.map((subItem, index) => (
            <ActionMenuItem key={`${keyPrefix}-${index}`} keyPrefix={`${keyPrefix}-${index}`} tableMetaData={table} menuItem={subItem} record={record} actions={actions} closeMenu={closeMenu} itemsShown={itemsShown} accordionStorageScope={accordionStorageScope} />
         ))} </>;
      }
      case "SUB_MENU":
      {
         if (!items || !items.some(item => wouldItemRender(item, table, actions)))
         {
            return <></>;
         }

         const subMenu: QMenu = new QMenu({...menuItem});
         subMenu.items = items;

         const layout = menuItem?.values?.get("layout");
         if (layout === "ACCORDION")
         {
            return <ActionAccordionSubMenu keyPrefix={`${keyPrefix}-sub`} tableMetaData={table} subMenuItem={menuItem} menu={subMenu} actions={actions} closeMenu={closeMenu} record={record} itemsShown={itemsShown} accordionStorageScope={accordionStorageScope} />;
         }

         return <ActionFlyOutSubMenu keyPrefix={`${keyPrefix}-sub`} tableMetaData={table} subMenuItem={menuItem} menu={subMenu} actions={actions} closeMenu={closeMenu} record={record} itemsShown={itemsShown} accordionStorageScope={accordionStorageScope} />;
      }
      case "BUILT_IN":
      {
         /////////////////////////////////////////////////////////////////////////////////
         // suppress (and warn about) any built-in whose handler isn't supplied by the  //
         // current screen — e.g., a query screen menu using NEW, or a view screen menu //
         // using BULK_LOAD.  better to silently hide than crash on click.              //
         /////////////////////////////////////////////////////////////////////////////////
         if (!isBuiltInHandlerAvailable(option, actions))
         {
            console.warn(`No handler available for built-in menu option [${option}] - suppressing menu item.`);
            return <></>;
         }

         switch (option)
         {
            case "NEW":
            {
               return table?.capabilities.has(Capability.TABLE_INSERT) && table?.insertPermission
                  ? itemsShown.addItem(menuItem) && <MenuItem onClick={() => actions.new(closeMenu)} data-qqq-id="menu-item-new">{content(menuItem, "add", "New")}</MenuItem>
                  : <></>;
            }
            case "COPY":
            {
               return table?.capabilities.has(Capability.TABLE_INSERT) && table?.insertPermission
                  ? <MenuItem onClick={() => actions.copy(closeMenu)} data-qqq-id="menu-item-copy">{content(menuItem, "copy", "Copy")}</MenuItem>
                  : <></>;
            }
            case "EDIT":
            {
               return table?.capabilities.has(Capability.TABLE_UPDATE) && table?.editPermission
                  ? <MenuItem onClick={() => actions.edit(closeMenu)} data-qqq-id="menu-item-edit">{content(menuItem, "edit", "Edit")}</MenuItem>
                  : <></>;
            }
            case "DELETE":
            {
               return table?.capabilities.has(Capability.TABLE_DELETE) && table?.deletePermission
                  ? <MenuItem onClick={() => actions.delete(closeMenu)} data-qqq-id="menu-item-delete">{content(menuItem, "delete", "Delete")}</MenuItem>
                  : <></>;
            }
            case "DEVELOPER_MODE":
            {
               return <MenuItem onClick={() => actions.developerMode(closeMenu)}>{content(menuItem, "code", "Developer Mode")}</MenuItem>;
            }
            case "AUDIT":
            {
               return actions.getMetaData()?.tables?.has("audit")
                  ? <MenuItem onClick={() => actions.audit(closeMenu)} data-qqq-id="menu-item-audit">{content(menuItem, "checklist", "Audit")}</MenuItem>
                  : <></>;
            }
            case "THIS_TABLE_PROCESS_LIST":
            {
               return <> {showProcessesFromList(actions, itemsShown, menuItem, actions.getTableProcesses(), closeMenu, keyPrefix)} </>;
            }
            case "ALL_TABLES_PROCESS_LIST":
            {
               return <> {showProcessesFromList(actions, itemsShown, menuItem, actions.getGenericProcesses(), closeMenu, keyPrefix)} </>;
            }
            case "BULK_LOAD":
            {
               return table?.capabilities.has(Capability.TABLE_INSERT) && table?.insertPermission && actions.getMetaData()?.processes?.has(`${table.name}.bulkInsert`)
                  ? itemsShown.addItem(menuItem) && <MenuItem onClick={() =>
                  {
                     closeMenu?.();
                     actions.bulkLoad();
                  }} data-qqq-id="menu-item-bulk-load">{content(menuItem, "library_add", "Bulk Load")}</MenuItem>
                  : <></>;
            }
            case "BULK_EDIT":
            {
               return table?.capabilities.has(Capability.TABLE_UPDATE) && table?.editPermission && actions.getMetaData()?.processes?.has(`${table.name}.bulkEdit`)
                  ? itemsShown.addItem(menuItem) && <MenuItem onClick={() =>
                  {
                     closeMenu?.();
                     actions.bulkEdit();
                  }} data-qqq-id="menu-item-bulk-edit">{content(menuItem, "edit", "Bulk Edit")}</MenuItem>
                  : <></>;
            }
            case "BULK_EDIT_WITH_FILE":
            {
               return table?.capabilities.has(Capability.TABLE_UPDATE) && table?.editPermission && actions.getMetaData()?.processes?.has(`${table.name}.bulkEditWithFile`)
                  ? itemsShown.addItem(menuItem) && <MenuItem onClick={() =>
                  {
                     closeMenu?.();
                     actions.bulkEditWithFile();
                  }} data-qqq-id="menu-item-bulk-edit-with-file">{content(menuItem, "edit_note", "Bulk Edit With File")}</MenuItem>
                  : <></>;
            }
            case "BULK_DELETE":
            {
               return table?.capabilities.has(Capability.TABLE_DELETE) && table?.deletePermission && actions.getMetaData()?.processes?.has(`${table.name}.bulkDelete`)
                  ? itemsShown.addItem(menuItem) && <MenuItem onClick={() =>
                  {
                     closeMenu?.();
                     actions.bulkDelete();
                  }} data-qqq-id="menu-item-bulk-delete">{content(menuItem, "delete", "Bulk Delete")}</MenuItem>
                  : <></>;
            }
         }
      }
   }

   console.warn(`Unrecognized menu item: type:[${menuItem.itemType}], values:[${JSON.stringify(menuItem.values)}]`);
   return (<></>);
}


/////////////////////////////////////////
// component that is a fly-out submenu //
/////////////////////////////////////////
interface ActionSubMenuProps
{
   tableMetaData: QTableMetaData,
   record?: QRecord,
   menu: QMenu,
   subMenuItem: QMenuItem,
   actions: ActionMenuActions,
   closeMenu?: () => void,
   itemsShown?: ItemsShownInMenu
   keyPrefix?: string
   accordionStorageScope?: "query" | "view"
}

function ActionFlyOutSubMenu({actions, menu, tableMetaData, record, subMenuItem, closeMenu: closeParentMenu, itemsShown, keyPrefix, accordionStorageScope = "view"}: ActionSubMenuProps): JSX.Element
{
   const [menuElement, setMenuElement] = useState(null);
   const openMenu = (event: any) => setMenuElement(event.currentTarget);
   const closeSubMenu = () => setMenuElement(null);

   itemsShown.addItem(subMenuItem);

   return (<>
      <MenuItem onClick={openMenu}>
         {content(subMenuItem, "double_arrow", menu.label, "chevron_right")}
      </MenuItem>
      <Menu
         anchorEl={menuElement}
         anchorOrigin={{vertical: "top", horizontal: "left",}}
         transformOrigin={{vertical: "top", horizontal: "right",}}
         open={Boolean(menuElement)}
         onClose={closeSubMenu}
         keepMounted
         data-qqq-id="record-view-actions-sub-menu"
      >
         {menu.items.map((item, index) => (
            <ActionMenuItem key={`${keyPrefix}-${index}`} keyPrefix={`${keyPrefix}-${index}`} tableMetaData={tableMetaData} menuItem={item} record={record} actions={actions} closeMenu={() =>
            {
               closeSubMenu();
               closeParentMenu?.();
            }} itemsShown={itemsShown} accordionStorageScope={accordionStorageScope} />
         ))}
      </Menu>
   </>);
}


////////////////////////////////////////////
// component that is an accordion submenu //
////////////////////////////////////////////
function ActionAccordionSubMenu({actions, menu, tableMetaData, record, subMenuItem, closeMenu, itemsShown, keyPrefix, accordionStorageScope = "view"}: ActionSubMenuProps): JSX.Element
{
   const storageKey = `qqq.subMenu.accordionState.${accordionStorageScope}.${tableMetaData.name}.${subMenuItem.label ?? menu.label}`;

   const [open, setOpen] = useState(() =>
   {
      const stored = localStorage.getItem(storageKey);
      return stored === "true";
   });

   const toggleOpen = () =>
   {
      const newOpen = !open;
      setOpen(newOpen);
      localStorage.setItem(storageKey, String(newOpen));
   };

   itemsShown.addItem(subMenuItem);

   return (<>
      <MenuItem onClick={(event) =>
      {
         toggleOpen();
         (event.currentTarget as HTMLElement).blur();
      }}>
         {content(subMenuItem, undefined, menu.label, open ? "expand_less" : "expand_more")}
      </MenuItem>
      <Collapse in={open}>
         <Box sx={{pl: 2}}>
            {menu.items.map((item, index) => (
               <ActionMenuItem key={`${keyPrefix}-${index}`} keyPrefix={`${keyPrefix}-${index}`} tableMetaData={tableMetaData} menuItem={item} record={record} actions={actions} closeMenu={closeMenu} itemsShown={itemsShown} accordionStorageScope={accordionStorageScope} />
            ))}
         </Box>
      </Collapse>
   </>);
}


/***************************************************************************
 * output the content for a menu item - a box, an icon, a label.
 ***************************************************************************/
function content(menuItem: QMenuItem, defaultIconName: string | undefined, defaultLabel: string, rightIconName?: string): JSX.Element
{
   const leftIconName = menuItem.icon?.name ?? defaultIconName;

   return <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" sx={{"& .MuiListItemIcon-root": {minWidth: "auto"}}}>
      <Box display="flex" alignItems="center" sx={{"& .MuiListItemIcon-root": {minWidth: "30px"}}}>
         {leftIconName && <ListItemIcon><Icon>{leftIconName}</Icon></ListItemIcon>}
         {menuItem.label ?? defaultLabel}
      </Box>
      {rightIconName && <ListItemIcon><Icon>{rightIconName}</Icon></ListItemIcon>}
   </Box>;
}


/***************************************************************************
 * render processes from a list, if they exist and haven't been shown before.
 ***************************************************************************/
function showProcessesFromList(actions: ActionMenuActions, itemsShown: ItemsShownInMenu, menuItem: QMenuItem, processes: QProcessMetaData[], closeMenu?: () => void, keyPrefix: string = "")
{
   return processes?.map((process, index) =>
   {
      const key = `${keyPrefix}-${index}`;
      if (!process)
      {
         return <React.Fragment key={key}></React.Fragment>;
      }

      if (itemsShown && itemsShown?.hasProcessBeenShown(process.name))
      {
         return <React.Fragment key={key}></React.Fragment>;
      }

      itemsShown?.addItem(menuItem);

      return (
         <MenuItem key={key} onClick={() =>
         {
            closeMenu?.();
            actions.runProcess(process.name);
         }} data-qqq-id={`menu-item-${sanitizeId(process.name)}`}>
            {content(new QMenuItem({label: process.label, icon: {name: process.iconName}}), "arrow_forward", process.label)}
         </MenuItem>
      );
   });
}


/***************************************************************************
 * Helper class to keep track of which items have been shown in a menu.
 * So that the built-ins that show process lists can avoid showing processes
 * that were show individually, and to help avoid back-to-back dividers.
 ***************************************************************************/
export class ItemsShownInMenu
{
   public items: QMenuItem[] = [];

   /***************************************************************************
    * Add a shown-item to this object.  Always returns true (so that it can
    * be used in a addItem() && <>showTheItem</> kind of expression.
    ***************************************************************************/
   public addItem(item: QMenuItem): true
   {
      this.items.push(item);
      return true;
   }

   /***************************************************************************
    * test if a process has previously been shown.
    *
    * meant for use by the THIS_TABLE_PROCESS_LIST built-in, to show processes
    * that weren't otherwise shown.
    *
    * Some possible improvements may be:
    * - looking into the future (e.g., will a process be rendered *after* the built-in?)
    * - should it look across menus (right now only looks at one).
    ***************************************************************************/
   public hasProcessBeenShown(processName: string): boolean
   {
      for (let shownItem of this.items)
      {
         const shownProcessName = shownItem?.values?.get("processName");
         if (processName == shownProcessName)
         {
            return true;
         }
      }

      return false;
   }

   /***************************************************************************
    * test if it's okay to show a divider now.  it's not okay to show a divider
    * if the last item was a divider, or as the first item.
    * TODO - would be nice to also not show a divider as the last item, but that
    * requires looking into the future some (as it's not just the size of the
    * menu, but also about permissions).  The approach could maybe be to put
    * an id on each shown divider, and to hide it if it's the last item?
    * then again, could we just CSS hide :first, :last, and siblings?
    ***************************************************************************/
   public okayToShowDivider(): boolean
   {
      if (this.items.length == 0)
      {
         return false;
      }

      if (this.items[this.items.length - 1].itemType == "DIVIDER")
      {
         return false;
      }

      return true;
   }

}


// todo - after theme code is merged back in, use this function from util file:
// import {sanitizeId} from "qqq/utils/qqqIdUtils";
function sanitizeId(text: string): string
{
   if (!text)
   {
      return "";
   }

   return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .substring(0, 50);
}
